import React, { useEffect, useRef } from 'react';
import Icon from '../AppIcon';
import { bloccaScroll } from './scrollLock';

const LARGHEZZE = {
  md: 'md:max-w-xl',
  lg: 'md:max-w-2xl',
  xl: 'md:max-w-4xl',
};

/**
 * Modal con pannello centrato su desktop e bottom-sheet a tutta altezza su mobile.
 * Props: isOpen, onClose, titolo, etichetta (testo piccolo sopra al titolo), larghezza (md|lg|xl),
 * footer (nodo), intestazioneDestra (nodo mostrato accanto alla X), bloccaChiusura (disabilita Esc/backdrop/X, es. durante il salvataggio).
 */
const Modal = ({
  isOpen,
  onClose,
  titolo,
  etichetta,
  larghezza = 'lg',
  footer,
  intestazioneDestra,
  bloccaChiusura = false,
  children,
}) => {
  const onCloseRef = useRef(onClose);
  const bloccaRef = useRef(bloccaChiusura);
  onCloseRef.current = onClose;
  bloccaRef.current = bloccaChiusura;

  useEffect(() => {
    if (!isOpen) return undefined;
    const handler = (e) => {
      if (e.key === 'Escape' && !bloccaRef.current) onCloseRef.current?.();
    };
    window.addEventListener('keydown', handler);
    const sblocca = bloccaScroll();
    return () => {
      window.removeEventListener('keydown', handler);
      sblocca();
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-0 md:p-6 overflow-hidden">
      <div
        className="absolute inset-0 bg-background/80 backdrop-blur-xl animate-in fade-in duration-300"
        onClick={() => { if (!bloccaChiusura) onClose?.(); }}
      />

      <div
        role="dialog"
        aria-modal="true"
        className={`relative w-full h-full md:h-auto md:max-h-[88dvh] ${LARGHEZZE[larghezza] || LARGHEZZE.lg} bg-card border-t md:border border-border/60 rounded-t-[2.5rem] md:rounded-[2.5rem] shadow-2xl flex flex-col animate-in slide-in-from-bottom-10 md:zoom-in-95 duration-300`}
      >
        {/* Drag handle mobile */}
        <div className="md:hidden flex justify-center pt-3 pb-1 shrink-0">
          <div className="w-12 h-1.5 rounded-full bg-muted/50" />
        </div>

        {/* Header */}
        <div className="shrink-0 px-6 md:px-8 pt-4 md:pt-7 pb-5 border-b border-border/40">
          <div className="flex items-start justify-between gap-4">
            <div className="flex flex-col gap-2 min-w-0 flex-1">
              {etichetta && (
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full bg-primary" />
                  <span className="text-[10px] font-black uppercase tracking-[.3em] text-muted-foreground/60">
                    {etichetta}
                  </span>
                </div>
              )}
              <h3 className="font-black uppercase tracking-tighter text-foreground leading-[1.1] break-words text-xl md:text-2xl">
                {titolo}
              </h3>
            </div>
            {intestazioneDestra && <div className="shrink-0">{intestazioneDestra}</div>}
            <button
              type="button"
              onClick={() => { if (!bloccaChiusura) onClose?.(); }}
              disabled={bloccaChiusura}
              aria-label="Chiudi"
              className="shrink-0 w-10 h-10 rounded-2xl bg-muted/50 hover:bg-red-500/10 border border-border/40 hover:border-red-500/20 text-muted-foreground hover:text-red-500 flex items-center justify-center transition-all shadow-sm disabled:opacity-50"
            >
              <Icon name="X" size={20} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 md:p-8 space-y-6 scrollbar-hide">
          {children}
        </div>

        {/* Footer */}
        {footer && (
          <div className="shrink-0 px-6 md:px-8 py-5 border-t border-border/40 bg-muted/10 flex items-center justify-end gap-3">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

export default Modal;
